import { SvgIconComponent } from "@mui/icons-material";
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ComputerIcon from '@mui/icons-material/Computer';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import FolderIcon from '@mui/icons-material/Folder';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import GridViewIcon from '@mui/icons-material/GridView';
import HubIcon from '@mui/icons-material/Hub';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyIcon from '@mui/icons-material/Key';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { Autocomplete } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { WidgetProps } from '@rjsf/utils';
import { ApiAzureDevOpsPipeline, ApiAzureDevOpsProcess, ApiAzureDevOpsProject, ApiAzureEntraGroup, ApiAzureEntraServicePrincipals, ApiAzureEntraUser, ApiAzureLocation, ApiAzureResourceGroup, ApiAzureSubscription, ApiCustomProjectData, ApiDelineaFolderLookupItem, ApiDelineaPlatformUser, ApiServiceNowBusinessService, ApiServiceNowUserGroup, ApiVSphereCluster, ApiVSphereDatacenter, ApiVSphereDatastore, ApiVSphereDatastoreCluster, ApiVSphereFolder, ApiVSphereHost, ApiVSphereNetwork, ApiVSphereTagCategories, ApiVSphereTags } from "@shared/interfaces/ApiInterfaces";
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderWithEval } from "utils/utils";
import { getAccessToken } from '../utils/accessTokenStore';
import BackendApiService from '../services/BackendApiService';

const vSphereStorage = (free_space: number, capacity: number) => { return `free ${((free_space / capacity) * 100).toFixed(0)}% / ${(free_space / (1024 * 1024 * 1024)).toFixed(2)} GB available of ${(capacity / (1024 * 1024 * 1024)).toFixed(2)} GB`; }
const truncateText = (value: string, maxLength: number) => {
  if (!value) {
    return "";
  }
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
};

const inputDataConfigs: { [key: string]: EndpointConfig<any> } = {
  "servicenow-businessservices": {
    requestFunction: (apiClient: BackendApiService) => apiClient.getServiceNowBusinessServices(),
    valueAttribute: "name",
    valueFunction: (v) => (v?.name && v?.number) ? `${v?.name} (${v?.number})` : v,
    isOptionEqualToValue: (option, value) => option.name === value?.name,
    getOptionLabel: (option) => `${option.name} (${option.number})`,
    initCheck: true,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiServiceNowBusinessService>,
  "servicenow-usergroups": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getServiceNowUserGroups(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    isOptionEqualToValue: (option, value) => option.name === value?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => PeopleOutlineIcon,
    initCheck: false,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiServiceNowUserGroup>,
  "customprojects": {
    requestFunction: (apiClient: BackendApiService) => apiClient.getCustomProjects(),
    valueAttribute: "customProject",
    getOptionLabel: (option) => option.customProject,
    initCheck: true,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiCustomProjectData>,
  "azure-subscriptions": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getAzureSubscriptions(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.displayName}`,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiAzureSubscription>,
  "azure-locations": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getAzureLocations(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.displayName}`,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiAzureLocation>,
  "azure-resourcegroups": {
    requestFunction: (apiClient: BackendApiService, formContext: any, properties: any) => {
      if (formContext.subscription) {
        return apiClient.getAzureResourceGroups(properties);
      }
      return [];
    }, // TODO: Remove subscription here
    valueAttribute: "name",
    // valueFunction: (v) => v?.name,
    // isOptionEqualToValue: (option, value) => option.name === value?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: (option) => option.customProject !== "" ? DashboardCustomizeIcon : GridViewIcon,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiAzureResourceGroup>,
  "azure-serviceprincipals": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getServicePrincipals(properties),
    valueAttribute: "displayName",
    valueFunction: (v) => v?.id,
    getOptionLabel: (option) => `${option.displayName} (${option.id}) [${option.servicePrincipalType}]`,
    getOptionIcon: (option) => option.servicePrincipalType === "ManagedIdentity" ? KeyIcon : ViewInArIcon,
    initCheck: true,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiAzureEntraServicePrincipals>,
  "azure-entragroups": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getEntraGroups(properties),
    valueAttribute: "displayName",
    valueFunction: (v) => v?.id,
    getOptionLabel: (option) => `${option.displayName} (${option.id})`,
    getOptionIcon: () => PeopleOutlineIcon,
    initCheck: true,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiAzureEntraGroup>,
  "azure-entrausers": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getEntraUsers(properties),
    valueAttribute: "displayName",
    valueFunction: (v, properties) => {
      const use = properties.use?.toLowerCase();
      if (use.includes("userprincipalname")) {
        return use.includes("manager.") ? v?.manager?.userPrincipalName || "no manager defined" : v?.userPrincipalName || "not defined";
      } else {
        return use.includes("manager.") ? v?.manager?.mailNickname || "no manager defined" : v?.mailNickname || "not defined";
      }
    },
    getOptionLabel: (option) => `${option.displayName} (${option.manager?.mail || option.mail})`,
    getOptionIcon: (option) => option.manager ? ManageAccountsIcon : PersonOutlineIcon,
    initCheck: false,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiAzureEntraUser>,
  "delinea-platformusers": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getDelineaPlatformUsers(properties),
    valueAttribute: "displayName",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.displayName} (${option.name})`,
    getOptionIcon: () => PersonOutlineIcon,
    initCheck: false,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiDelineaPlatformUser>,
  "azuredevops-projects": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getAzureDevOpsProjects(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}${option.description ? ` (${option.description})` : ''}`,
    getOptionIcon: () => WorkspacesIcon,
    initCheck: false,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiAzureDevOpsProject>,
  "azuredevops-processes": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getAzureDevOpsProcesses(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}${option.description ? ` (${option.description})` : ''}`,
    getOptionIcon: () => HubIcon,
    initCheck: false,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiAzureDevOpsProcess>,
  "azuredevops-pipelines": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getAzureDevOpsPipelines(properties),
    valueAttribute: "id",
    valueFunction: (v) => v?.id,
    getOptionLabel: (option) => `${option.name}${option.path ? ` (${option.path})` : ''}`,
    getOptionIcon: () => AltRouteIcon,
    initCheck: false,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiAzureDevOpsPipeline>,
  "delinea-folders-nonexisting": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getDelineaFoldersLookup(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => FolderIcon,
    initCheck: false,
    conditionalFieldCheck: false,
    allowFreeText: true,
    nonExistingOnly: true,
    existingValueErrorMessage: "Folder already exists"
  } as EndpointConfig<ApiDelineaFolderLookupItem>,
  "vsphere-datacenters": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getVSphereDatacenters(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => HubIcon,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiVSphereDatacenter>,
  "vsphere-clusters": {
    requestFunction: (apiClient: BackendApiService, _formContext: any, properties: any) => apiClient.getVSphereClusters(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => HubIcon,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiVSphereCluster>,
  "vsphere-hosts": {
    requestFunction: (apiClient: BackendApiService, _formContext: any, properties: any) => apiClient.getVSphereHosts(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => ComputerIcon,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiVSphereHost>,
  "vsphere-folders": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getVSphereFolders(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => FolderIcon,
    initCheck: true,
    conditionalFieldCheck: false,
    allowFreeText: true
  } as EndpointConfig<ApiVSphereFolder>,
  "vsphere-datastores": {
    requestFunction: (apiClient: BackendApiService, _formContext: any, properties: any) => apiClient.getVSphereDatastores(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name} (${vSphereStorage(option.free_space, option.capacity)})`,
    getOptionIcon: () => FolderIcon,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiVSphereDatastore>,
  "vsphere-datastoreclusters": {
    requestFunction: (apiClient: BackendApiService, _formContext: any, properties: any) => apiClient.getVSphereDatastoreClusters(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name} (${vSphereStorage(option.free_space, option.capacity)})`,
    getOptionIcon: () => FolderCopyIcon,
    initCheck: true,
    conditionalFieldCheck: true
  } as EndpointConfig<ApiVSphereDatastoreCluster>,
  "vsphere-networks": {
    requestFunction: (apiClient: BackendApiService, _formContext: any, properties: any) => apiClient.getVSphereNetworks(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => HubIcon,
    initCheck: true,
    conditionalFieldCheck: true,
    postAction: async (apiClient: BackendApiService, _formContext: any, properties: any) => {
      return await apiClient.getVitalQIPAddressLookup(properties);
    }
  } as EndpointConfig<ApiVSphereNetwork>,
  "vsphere-tagcategories": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getVSphereTagCategories(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => BookmarkBorderIcon,
    initCheck: true,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiVSphereTagCategories>,
  "vsphere-tags": {
    requestFunction: (apiClient: BackendApiService, _formContext: undefined, properties: any) => apiClient.getVSphereTags(properties),
    valueAttribute: "name",
    valueFunction: (v) => v?.name,
    getOptionLabel: (option) => `${option.name}`,
    getOptionIcon: () => BookmarkBorderIcon,
    initCheck: true,
    conditionalFieldCheck: false
  } as EndpointConfig<ApiVSphereTags>,
};

class EndpointConfig<T> {
  requestFunction: (apiClient: BackendApiService, formContext: any, properties: any) => Promise<T[]>;
  valueAttribute?: keyof T;
  valueFunction?: (value: T | null, properties: any) => any;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  getOptionLabel: (option: T) => string;
  getOptionIcon?: (option: T) => SvgIconComponent;
  initCheck: boolean;
  conditionalFieldCheck: boolean;
  postAction?: (apiClient: BackendApiService, formContext: any, properties: any) => any;
  allowFreeText?: boolean;
  nonExistingOnly?: boolean;
  existingValueErrorMessage?: string;
}

function RenderJson({ data }: any) {
  if (Array.isArray(data)) {
    return (
      <ul>
        {data.map((item, idx) => (
          <li key={idx}><RenderJson data={item} /></li>
        ))}
      </ul>
    );
  } else if (data && typeof data === 'object') {
    return (
      <ul>
        {Object.entries(data).map(([key, value]) => (
          <li key={key}>
            <strong>{key}:</strong> <RenderJson data={value} />
          </li>
        ))}
      </ul>
    );
  } else {
    return <span>{String(data)}</span>;
  }
}

function FormAutocomplete(props: WidgetProps) {
  const { options, value } = props;

  const [open, setOpen] = useState(false);
  const [autocompleteValue, setAutocompleteValue] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [postActionResultLoading, setPostActionResultLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [postActionResult, setPostActionResult] = useState<any>(null);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [optionText, setOptionText] = useState('Start typing for loading data ...');
  const [loadedOptions, setLoadedOptions] = useState<any[]>([]);
  const lastEmittedValueRef = useRef<string>("__init__");
  const requestCacheRef = useRef<Map<string, any[]>>(new Map());
  const inFlightRequestsRef = useRef<Map<string, Promise<any[]>>>(new Map());
  const previousRenderedInputDataPropsKeyRef = useRef<string | null>(null);
  const previousRenderedInputDataPropsRef = useRef<Record<string, any> | null>(null);
  const suppressWarnRef = useRef<boolean>(false);

  const [accessToken] = useState(() => {
    return getAccessToken();
  });

  const inputDataConfig = useMemo(
    () => inputDataConfigs[options.inputData as string || ''],
    [options.inputData]
  );
  const isFieldDisabled = Boolean(props.disabled);
  const hasSchemaError = !isFieldDisabled && Array.isArray(props.rawErrors) && props.rawErrors.length > 0;
  const isModifyMode = Boolean((props.formContext as any)?.__isModify);

  const emitChange = useCallback((nextValue: any, message?: string) => {
    const fieldErrorSchema = message ? { __errors: [message] } : undefined;
    props.onChange(nextValue, fieldErrorSchema as any, props.id);
  }, [props]);

  const inputDataProperties = useMemo(
    () => options.inputDataProperties || {},
    [options.inputDataProperties]
  );

  const renderedInputDataProps = useMemo(
    () => Object.fromEntries(
      Object.entries(inputDataProperties).map(([key, template]) => [key, renderWithEval(String(template), props.formContext)])
    ),
    [inputDataProperties, props.formContext]
  );

  const renderedInputDataPropsKey = useMemo(
    () => JSON.stringify(renderedInputDataProps),
    [renderedInputDataProps]
  );

  const hasMissingDependency = useMemo(() => {
    if (!inputDataConfig?.conditionalFieldCheck) {
      return false;
    }

    return Object.values(renderedInputDataProps || {}).some(
      (dependencyValue) => dependencyValue === undefined || dependencyValue === null || String(dependencyValue).trim() === ""
    );
  }, [inputDataConfig?.conditionalFieldCheck, renderedInputDataProps]);

  const hasNoSelectableOptions = useMemo(() => {
    if (hasMissingDependency || loading) {
      return false;
    }

    // Only apply the "no entries available" blocked state to fields that
    // are controlled by prerequisite dependency checks.
    if (!inputDataConfig?.conditionalFieldCheck) {
      return false;
    }

    if (!inputDataConfig?.initCheck || inputDataConfig?.allowFreeText || inputDataConfig?.nonExistingOnly) {
      return false;
    }

    const hasCurrentValue = !(
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "")
    );

    return hasInitialized && loadedOptions.length === 0 && !hasCurrentValue;
  }, [hasInitialized, hasMissingDependency, inputDataConfig?.allowFreeText, inputDataConfig?.conditionalFieldCheck, inputDataConfig?.initCheck, inputDataConfig?.nonExistingOnly, loadedOptions.length, loading, value]);

  const hasBlockedSelection = hasMissingDependency || hasNoSelectableOptions;

  const dependencyHighlightSx = useMemo(() => {
    if (!hasBlockedSelection) {
      return undefined;
    }

    return {
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#f3f4f6',
        '&:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
          borderColor: '#9ea5af'
        },
        '&:hover:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
          borderColor: '#8e96a1'
        },
        '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
          borderColor: '#7f8894'
        }
      }
    };
  }, [hasBlockedSelection]);

  const dependencyHintText = useMemo(() => {
    if (hasMissingDependency) {
      return 'Please complete the prerequisite selection first.';
    }

    if (hasNoSelectableOptions) {
      return 'No entries available for the current selection.';
    }

    return '';
  }, [hasMissingDependency, hasNoSelectableOptions]);

  const dependencyLabel = useMemo(() => {
    if (!hasBlockedSelection) {
      return props.label;
    }

    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
        <Tooltip title={dependencyHintText} placement="top" arrow>
          <InfoOutlinedIcon
            sx={{
              fontSize: 16,
              color: '#6b7280',
              cursor: 'help',
              transition: 'color 160ms ease',
              '&:hover': {
                color: '#1f2937'
              }
            }}
          />
        </Tooltip>
        <span>{props.label}</span>
      </Box>
    );
  }, [dependencyHintText, hasBlockedSelection, props.label]);

  const additionalProps = inputDataConfig.initCheck
    ? {}
    : inputDataConfig.nonExistingOnly
      ? { filterOptions: () => [] }
      : { filterOptions: (options: any) => options };

  const hasExistingExactMatch = useCallback((query: string, options: any[]) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return false;
    }

    return options.some((option) => {
      const candidate = String(option?.name || option?.displayName || "").trim().toLowerCase();
      return candidate === normalizedQuery;
    });
  }, []);

  const commitFreeTextValue = useCallback((rawValue: string) => {
    if (!inputDataConfig?.allowFreeText || inputDataConfig?.nonExistingOnly) {
      return;
    }

    const nextValue = String(rawValue || '').trim();
    if (nextValue !== String(value || '').trim()) {
      emitChange(nextValue);
    }
  }, [emitChange, inputDataConfig?.allowFreeText, inputDataConfig?.nonExistingOnly, value]);

  const loadOptions = useCallback(async (input: string, formContext: any) => {
    setLoading(true);

    const backendApiServiceObj = new BackendApiService(accessToken ?? '');
    const requestFunction = inputDataConfig?.requestFunction;
    const postAction = inputDataConfig?.postAction;

    let result: any[] = [];
    // only call requestFunction if formContext was initially rendered
    if (formContext && Object.keys(formContext || {})?.length !== 0) {
      const searchValue = inputDataConfig.initCheck ? '__init__' : input;
      const cacheKey = `${String(options.inputData)}|${renderedInputDataPropsKey}|${String(searchValue).toLowerCase()}`;

      const cachedResult = requestCacheRef.current.get(cacheKey);
      if (cachedResult) {
        setLoadedOptions(cachedResult);
        setLoading(false);
        return cachedResult;
      }

      const existingRequest = inFlightRequestsRef.current.get(cacheKey);
      if (existingRequest) {
        result = await existingRequest;
      } else {
        const requestPromise = Promise.resolve(
          requestFunction(backendApiServiceObj, formContext, { ...renderedInputDataProps as object, searchValue: inputDataConfig.initCheck ? undefined : input })
        )
          .then((response) => response || [])
          .finally(() => {
            inFlightRequestsRef.current.delete(cacheKey);
          });

        inFlightRequestsRef.current.set(cacheKey, requestPromise);
        result = await requestPromise;

        // Keep cache bounded to avoid unbounded growth in long sessions.
        if (requestCacheRef.current.size > 200) {
          const firstKey = requestCacheRef.current.keys().next().value;
          if (firstKey) {
            requestCacheRef.current.delete(firstKey);
          }
        }
        requestCacheRef.current.set(cacheKey, result);
      }

      setLoadedOptions(result);
      if (postAction && input !== "") {
        setPostActionResultLoading(true);
        setPostActionResult(await postAction(backendApiServiceObj, formContext, { ...renderedInputDataProps as object, selectedValue: input }));
        setPostActionResultLoading(false);
      }
    }
    setLoading(false);

    if (result && result.length === 0) {
      setOptionText("No entry found");
    }

    return result;
  }, [accessToken, inputDataConfig, options.inputData, renderedInputDataProps, renderedInputDataPropsKey]);

  const valueFunction = (formContext: any, v: any) => {
    if (inputDataConfig?.valueFunction) {
      const renderedInputDataProps = Object.fromEntries(
        Object.entries(inputDataProperties).map(([key, template]) => [key, renderWithEval(template, formContext)])
      );
      return inputDataConfig?.valueFunction(v, renderedInputDataProps);
    } else if (inputDataConfig?.valueAttribute) {
      return v[inputDataConfig.valueAttribute];
    } else {
      return v;
    }
  };

  const isOptionEqualToValue = (option: any, value: any) => {
    if (inputDataConfig?.isOptionEqualToValue) {
      return inputDataConfig?.isOptionEqualToValue(option, value);
    } else if (inputDataConfig?.valueAttribute && value?.[inputDataConfig.valueAttribute!]) {
      return option[inputDataConfig.valueAttribute!] === value?.[inputDataConfig.valueAttribute!];
    } else if (inputDataConfig?.valueAttribute) {
      return option[inputDataConfig.valueAttribute!] === value;
    } else {
      return option === value;
    }
  };

  const onOpen = async () => {
    setOpen(true);
    if (inputDataConfig.initCheck && !hasMissingDependency) {
      await loadOptions(inputValue, props.formContext);
      setHasInitialized(true);
    }
  };

  const debouncedFetchOptions = useCallback(
    debounce(async (query, formContext) => {
      if (query.length >= 3) {
        await loadOptions(query, formContext);
      }
    }, 900),
    [loadOptions]
  );

  useEffect(() => {
    const init = async () => {
      if (inputDataConfig.initCheck && !hasInitialized && !hasMissingDependency) {
        await loadOptions(String(value || ""), props.formContext);
        setHasInitialized(true);
      }

      const hasMeaningfulValue = !(
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "")
      );

      if (!hasMeaningfulValue) {
        suppressWarnRef.current = false;
        setAutocompleteValue(null);
        setValidationMessage("");
        if (!inputDataConfig.allowFreeText) {
          setInputValue("");
        }
        return;
      }

      if (inputDataConfig?.nonExistingOnly || inputDataConfig?.allowFreeText) {
        setInputValue(String(value));
        setAutocompleteValue(null);
        return;
      }

      if (inputDataConfig?.valueAttribute) {
        const loaded = await loadOptions(String(value), props.formContext);
        const findEntry = loaded.find(o => {
          const entryValue = valueFunction(props.formContext, o);
          return String(entryValue).toLowerCase() === String(value).toLowerCase();
        });

        setAutocompleteValue(findEntry || null);

        if (findEntry) {
          suppressWarnRef.current = false;
          setValidationMessage("");
          setInputValue(inputDataConfig.getOptionLabel(findEntry));
        } else {
          if (suppressWarnRef.current) {
            setValidationMessage("");
            setInputValue("");
            setAutocompleteValue(null);
            return;
          }

          setInputValue(String(value));
          setValidationMessage(isModifyMode && !isFieldDisabled ? "This value no longer exists in current options. Please choose a valid entry." : "");
        }
      } else {
        setValidationMessage("");
        setAutocompleteValue(value);
        setInputValue(typeof value === "string" ? value : inputDataConfig.getOptionLabel(value));
      }
    };

    init();
  }, [hasInitialized, hasMissingDependency, inputDataConfig?.allowFreeText, inputDataConfig?.initCheck, inputDataConfig?.nonExistingOnly, inputDataConfig?.valueAttribute, isModifyMode, loadOptions, options.inputData, props.formContext, renderedInputDataPropsKey, value]);

  useEffect(() => {
    if (hasMissingDependency) {
      setOpen(false);
      setHasInitialized(false);
      setLoadedOptions([]);
      setOptionText('Start typing for loading data ...');
    }
  }, [hasMissingDependency]);

  useEffect(() => {
    if (inputDataConfig.initCheck) {
      return;
    }

    if (value !== inputValue) {
      if (!inputDataConfig.nonExistingOnly) {
        setLoadedOptions([]);
        setPostActionResult(null);
      }
      if (inputValue.length >= 3) {
        setOptionText("Loading...");
        debouncedFetchOptions(inputValue, props.formContext);
      }
    }
  }, [debouncedFetchOptions, inputDataConfig.initCheck, inputDataConfig.nonExistingOnly, inputValue, props.formContext, value]);

  useEffect(() => {
    if (!inputDataConfig?.nonExistingOnly) {
      return;
    }

    const trimmedInput = String(inputValue || "").trim();
    if (!trimmedInput) {
      setValidationMessage("");
      if (lastEmittedValueRef.current !== "") {
        lastEmittedValueRef.current = "";
        emitChange("");
      }
      return;
    }

    if (trimmedInput.length < 3) {
      setValidationMessage("");
      if (lastEmittedValueRef.current !== trimmedInput) {
        lastEmittedValueRef.current = trimmedInput;
        emitChange(trimmedInput);
      }
      return;
    }

    const alreadyExists = hasExistingExactMatch(trimmedInput, loadedOptions);
    if (alreadyExists) {
      const message = inputDataConfig.existingValueErrorMessage || "Value already exists";
      setValidationMessage(message);
      if (lastEmittedValueRef.current !== "") {
        lastEmittedValueRef.current = "";
        emitChange("", message);
      }
      return;
    }

    setValidationMessage("");
    if (lastEmittedValueRef.current !== trimmedInput) {
      lastEmittedValueRef.current = trimmedInput;
      emitChange(trimmedInput);
    }
  }, [emitChange, inputDataConfig?.existingValueErrorMessage, inputDataConfig?.nonExistingOnly, inputValue, loadedOptions, hasExistingExactMatch]);

  useEffect(() => {
    if (!inputDataConfig?.conditionalFieldCheck) {
      return;
    }

    const previousKey = previousRenderedInputDataPropsKeyRef.current;
    const previousProps = previousRenderedInputDataPropsRef.current;

    if (previousKey === null) {
      previousRenderedInputDataPropsKeyRef.current = renderedInputDataPropsKey;
      previousRenderedInputDataPropsRef.current = renderedInputDataProps as Record<string, any>;
      return;
    }

    if (previousKey === renderedInputDataPropsKey) {
      return;
    }

    const hasMissingDependency = (propsToCheck: Record<string, any> | null) =>
      Object.values(propsToCheck || {}).some(
        (dependencyValue) => dependencyValue === undefined || dependencyValue === null || String(dependencyValue).trim() === ""
      );

    const hadMissingDependencyBefore = hasMissingDependency(previousProps);
    const hasMissingDependencyNow = hasMissingDependency(renderedInputDataProps as Record<string, any>);

    previousRenderedInputDataPropsKeyRef.current = renderedInputDataPropsKey;
    previousRenderedInputDataPropsRef.current = renderedInputDataProps as Record<string, any>;

    // Immediately clear stale options when dependency values change.
    setOpen(false);
    setLoadedOptions([]);
    setPostActionResult(null);
    setHasInitialized(false);
    setOptionText('Start typing for loading data ...');

    // Do not clear existing/default child values in this transition.
    if (hadMissingDependencyBefore && !hasMissingDependencyNow) {
      return;
    }

    const hasAnyValue = !!String(value || "") || !!String(inputValue || "") || !!autocompleteValue;
    if (!hasAnyValue) {
      return;
    }

    setInputValue("");
    setAutocompleteValue(null);
    setValidationMessage("");
    setPostActionResult(null);
    setLoadedOptions([]);
    suppressWarnRef.current = true;
    emitChange("");
  }, [autocompleteValue, emitChange, inputDataConfig?.conditionalFieldCheck, inputValue, renderedInputDataPropsKey, value]);

  return (
      <>
        <Autocomplete
          {...additionalProps}
          freeSolo={!!inputDataConfig.allowFreeText}
          PopperComponent={(props) => (
            <Popper {...props} sx={{ border: "1px solid #DDD", borderRadius: "5px" }}>{props.children}</Popper>
          )}
          open={open}
          onOpen={() => {
            onOpen();
          }}
          onClose={() => {
            setOpen(false);
          }}
          getOptionLabel={inputDataConfig?.getOptionLabel}
          options={loadedOptions}
          loading={loading}
          value={autocompleteValue}
          inputValue={String(inputValue || "")}
          noOptionsText={
            hasMissingDependency
              ? 'Please complete the prerequisite selection first.'
              : hasNoSelectableOptions
                ? 'No entries available for the current selection.'
              : inputDataConfig.initCheck
                ? 'No entry found'
                : inputValue.length < 3
                  ? 'Start typing for loading data ...'
                  : optionText
          }
          isOptionEqualToValue={isOptionEqualToValue}
                  disabled={Boolean(props.disabled) || hasBlockedSelection}
          onInputChange={(e, v, reason) => {
            setInputValue(v);
            if (reason === "input") {
              suppressWarnRef.current = false;
              setValidationMessage("");
            }

            // Also clear selected value on keyboard-deletion to empty input.
            if (!inputDataConfig.allowFreeText && reason === "input" && String(v || "") === "") {
              setAutocompleteValue(null);
              setValidationMessage("");
              emitChange("");
            }

            if (reason === "clear") {
              suppressWarnRef.current = false;
              setAutocompleteValue(null);
              setValidationMessage("");
              lastEmittedValueRef.current = "";
              emitChange("");
            }
          }}
          onChange={(e, v) => {
            suppressWarnRef.current = false;
            if (inputDataConfig.allowFreeText && typeof v === "string") {
              setInputValue(v);
              commitFreeTextValue(v);
              return;
            }
            setAutocompleteValue(v);
            if (v) {
              emitChange(valueFunction(props.formContext, v));
            } else {
              emitChange("");
            }
          }}
          renderOption={(props, option) => {
            const defaultLabel = inputDataConfig.getOptionLabel(option);
            const dropdownLabel = truncateText(String(defaultLabel), 100);

            return (
            <Box component="li" sx={{ '& > svg': { mr: 2, flexShrink: 0 }, fontSize: "10pt" }} {...props} key={defaultLabel} title={defaultLabel}>
              {inputDataConfig.getOptionIcon && inputDataConfig.getOptionIcon(option) && React.createElement(inputDataConfig.getOptionIcon(option), null, null)}
              {dropdownLabel}
            </Box>
            );
          }}
          renderInput={(params) => <TextField
            {...params}
            onBlur={(event) => {
              params.inputProps?.onBlur?.(event as any);
              if (inputDataConfig.allowFreeText && !inputDataConfig.nonExistingOnly) {
                commitFreeTextValue(String(inputValue || ''));
              }
            }}
            required={!!props.required}
            label={dependencyLabel}
            placeholder={props.placeholder || 'Start typing to search...'}
            fullWidth
            error={hasSchemaError || !!validationMessage}
            helperText={validationMessage || undefined}
            sx={dependencyHighlightSx}
            InputLabelProps={{
              ...params.InputLabelProps,
              style: {
                ...(params.InputLabelProps?.style || {}),
                pointerEvents: 'auto'
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />}
        />
        {postActionResultLoading ?
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
          :
          postActionResult && (
            <Box sx={{ mt: 1, color: 'text.secondary', fontSize: '0.95em' }}>
              <RenderJson data={postActionResult} />
            </Box>
          )}
      </>
  );
}

export default FormAutocomplete
